import { Body3 } from '@components/typography/Body3.tsx';
import { Icon } from '@components/Icon.tsx';
import { type IconType } from '@src/IconType.ts';
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SearchParamKeys } from '@funnellab/shared/src/types/SearchParamKeys';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { classNames } from '@utils/classNames.ts';
import { useSearchParams } from 'react-router-dom';
import useHorizontalScroll from '@hooks/useHorizontalScroll.ts';

export type Tab = {
  title: string | ReactNode;
  id: number | string;
  expandable?: boolean;
  IconLeading?: IconType;
  IconTrailing?: IconType;
  testId?: string;
  disableScrollingInTab?: boolean;
  tabPanelClassName?: string;
  additionalHeaderContent?: ReactNode;
  // Link tab can not be the default tab and non link tabs have to have content
} & (
  | { isLink: true; href: string; content?: JSX.Element; default?: never }
  | { isLink?: false; href?: never; content: JSX.Element; default?: boolean }
) &
  // Disabled tabs can not be the default tab
  (| { disabled: true; default?: never }
    | { disabled?: boolean; default?: boolean }
  );

export const Tabs = ({
  tabs,
  additionalHeaderNode,
  groupClassName,
  onSelectedIndexChange,
  tabLevel,
  variant = 'default',
}: Readonly<{
  tabs: Tab[];
  additionalHeaderNode?: ReactNode;
  groupClassName?: string;
  makeTabsTakeFullHeight?: boolean;
  tabLevel?: number;
  onSelectedIndexChange?: (index: number) => void;
  variant?: 'default' | 'underline' | 'greyfilled';
}>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabIndex = useMemo(() => {
    const tabIds = searchParams.getAll(SearchParamKeys.TAB);
    const tabIndex =
      tabLevel === undefined
        ? -1
        : tabs.findIndex((tab) => String(tab.id) === tabIds[tabLevel]);
    return tabIndex !== -1 ? tabIndex : 0;
  }, [searchParams, tabLevel, tabs]);
  const [selectedIndex, setSelectedIndex] = useState(initialTabIndex);
  const tabRefs = useRef<Map<number | string, HTMLElement>>(new Map());

  const updateSelectedIndex = useCallback(
    (newIndex: number) => {
      const selectedTab = tabs[newIndex];
      if (selectedTab?.content && !selectedTab.disabled) {
        setSelectedIndex(newIndex);
        onSelectedIndexChange?.(newIndex);
        const tabId = selectedTab.id;
        if (tabId) {
          tabRefs.current.get(tabId)?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }
    },
    [tabs, onSelectedIndexChange],
  );

  useEffect(() => {
    const selectedTab = tabs[selectedIndex];
    if (tabLevel === undefined || !selectedTab) {
      return;
    }
    const tabId = String(selectedTab.id);
    const tabParameter = searchParams.getAll(SearchParamKeys.TAB);
    if (tabParameter[tabLevel] === tabId) {
      return; // No need to update if the tab is already set
    }
    // update URL
    setSearchParams((prev) => {
      const tabCopy = [...tabParameter].filter((_v, i) => i <= tabLevel);
      tabCopy.splice(tabLevel, 1, tabId);
      prev.delete(SearchParamKeys.TAB);
      for (const tab of tabCopy) {
        prev.append(SearchParamKeys.TAB, tab);
      }
      return prev;
    });
  }, [tabLevel, searchParams, selectedIndex, setSearchParams, tabs]);

  useEffect(() => {
    tabRefs.current = new Map(
      tabs
        .map(({ id }) => [id, tabRefs.current.get(id)] as const)
        .filter((e): e is [number, HTMLElement] => !!e[1]),
    );
  }, [tabs]);

  const tabHeaderclassNamesByVariant = useMemo(() => {
    switch (variant) {
      case 'default':
        return classNames(
          'py-2 sm:py-3 px-4',
          'focus:outline-0 rounded-full md:first:rounded-l-xl md:last:rounded-r-xl md:rounded-none',
          'border-gray-200 data-[selected]:border-blue-500 md:data-[selected]:border-blue-100 md:border-blue-100 md:border-t md:border-b last:border-r-1 first:border-l-1',
          'data-[selected]:text-blue-500 data-[selected]:bg-blue-50 md:border-r-1',
          'disabled:text-gray-600 disabled:bg-zinc-700',
        );
      case 'underline':
        return classNames(
          'data-[selected]:text-blue-500 data-[selected]:border-b',
          '-mb-px',
          'py-2 px-3',
          'disabled:text-gray-600',
        );
      case 'greyfilled':
        return classNames(
          'p-1 group',
          'focus:outline-0 first:rounded-l-xl last:rounded-r-xl',
          'border-blue-100 border-t border-b last:border-r first:border-l',
          'disabled:text-gray-600 disabled:bg-zinc-700',
        );
    }
  }, [variant]);

  const tabPanelClassNamesByVariant = useMemo(() => {
    switch (variant) {
      case 'default':
        return classNames(
          'bg-white p-0 md:p-6 md:rounded-2xl md:border border-blue-100',
        );
      case 'underline':
        return classNames();
      case 'greyfilled':
        return classNames();
    }
  }, [variant]);

  const getDefaultSelectedIndexBorder = useCallback(
    (tabIndex: number) => {
      if (variant !== 'default') {
        return '';
      }
      if (tabIndex < selectedIndex) {
        return 'border-l md:border-l-0';
      } else if (tabIndex > selectedIndex) {
        return 'border-r md:border-r-0';
      } else {
        return 'border md:border-0';
      }
    },
    [variant, selectedIndex],
  );

  return (
    <TabGroup
      as="div"
      className={classNames('flex-1 flex flex-col min-h-0', groupClassName)}
      selectedIndex={selectedIndex}
      onChange={updateSelectedIndex}
    >
      <TabList className="flex justify-between mb-4">
        <div
          className={classNames(
            'overflow-x-auto scrollbar-hide flex-1 flex',
            variant !== 'underline' && 'rounded-xl',
          )}
          ref={useHorizontalScroll()}
        >
          <div className="w-full flex justify-between items-center">
            <div
              className={classNames(
                'flex flex-row bg-white',
                variant !== 'underline' && 'rounded-xl',
                variant === 'underline' && 'w-full border-b border-slate-300',
              )}
            >
              {tabs.map(
                (
                  {
                    id,
                    title,
                    disabled,
                    IconLeading,
                    IconTrailing,
                    testId,
                    isLink,
                    href,
                  },
                  tabIndex,
                ) => (
                  <Tab
                    key={id}
                    className={classNames(
                      getDefaultSelectedIndexBorder(tabIndex),
                      tabHeaderclassNamesByVariant,
                      'relative group',
                      'text-slate-900 text-sm font-medium leading-tight',
                      'enabled:cursor-pointer disabled:cursor-not-allowed',
                    )}
                    ref={(el) => {
                      if (el) {
                        tabRefs.current.set(id, el);
                      } else {
                        tabRefs.current.delete(id);
                      }
                    }}
                    disabled={disabled}
                    onClick={isLink ? () => window.open(href) : undefined}
                    data-testid={testId ? `tab-${testId}` : undefined}
                  >
                    <div
                      className={classNames(
                        'w-full flex relative items-center justify-center gap-x-1 md:gap-x-3',
                        variant === 'greyfilled' &&
                          'rounded-lg group-data-[selected]:bg-gray-100 px-3 py-2',
                      )}
                    >
                      {IconLeading && (
                        <Icon
                          type={IconLeading}
                          className="h-6 w-6 flex-shrink-0"
                        />
                      )}
                      <Body3
                        className={classNames(
                          'whitespace-nowrap',
                          variant === 'default' &&
                            'group-[&:not([data-selected])]:hidden md:group-[&:not([data-selected])]:block',
                        )}
                      >
                        {title}
                      </Body3>
                      {IconTrailing && (
                        <Icon
                          type={IconTrailing}
                          className="h-6 w-6 flex-shrink-0"
                        />
                      )}
                    </div>
                  </Tab>
                ),
              )}
            </div>
            {additionalHeaderNode && <>{additionalHeaderNode}</>}
            {tabs[selectedIndex]?.additionalHeaderContent && (
              <span className="flex h-full gap-1 ml-1">
                {tabs[selectedIndex].additionalHeaderContent}
              </span>
            )}
          </div>
        </div>
      </TabList>
      <TabPanels
        className={classNames(
          tabPanelClassNamesByVariant,
          'flex-auto flex-col max-h-[calc(100%-3.5rem)] h-full',
          !tabs[selectedIndex]?.disableScrollingInTab && 'overflow-auto',
          tabs[selectedIndex]?.tabPanelClassName,
        )}
      >
        {tabs.map(({ id, content, testId }) => {
          return (
            <TabPanel key={id} data-testid={testId} className="h-full">
              {content}
            </TabPanel>
          );
        })}
      </TabPanels>
    </TabGroup>
  );
};
